import { useState } from 'react'
import { Alert, Badge, Button, Group, Modal, Select, Stack, Text, TextInput } from '@mantine/core'
import { DataCard } from '@/components/ui'
import { EstadoDeConsulta } from '@/components/ui/EstadoDeConsulta'
import { useCriarSocTeam, useSocTeams, useSocTenants } from '@/hooks/useSoc'
import { nomeDerivado } from '@/pages/admin/socLabels'
import { EditorDeIntegrantes } from './EditorDeIntegrantes'
import { ConfiguracaoDeAlertas } from './ConfiguracaoDeAlertas'
import { TenantDoTime } from './TenantDoTime'
import { TetoDeLigacoes } from './TetoDeLigacoes'

const NIVEIS = ['1', '2', '3'] as const

/**
 * Aba "Times de SOC" da tela de Organização (Fase 5b / Task 4). O gestor hoje
 * não tem de onde ver os times de SOC dos tenants dele — esta aba consome
 * `GET /admin/soc/teams` (já em produção, Task 5b.1) e filtra por
 * `organization_id` no cliente, já que o servidor escopa a listagem inteira
 * (plataforma vê tudo; gestor só vê as organizações que administra).
 *
 * Duas afirmações que a tela NÃO pode deixar de fazer, e que são o ponto
 * dela — não enfeite:
 *
 * 1. Time DESLIGADO continua na lista (sumir foi o defeito que a 5b.1
 *    consertou), mas não pode PARECER coberto. `is_active` é a autoridade:
 *    `has_soc` do tenant desliga o time e as regras sem apagar o registro.
 * 2. Nível sem ninguém é dito por extenso — é exatamente o estado em que o
 *    alarme daquele nível não toca, e uma lista vazia não fala sozinha.
 *
 * Mestre-detalhe (Task 2): "Abrir" troca a lista pelo detalhe do time —
 * `EditorDeIntegrantes` e `ConfiguracaoDeAlertas`, os mesmos componentes que
 * hoje só vivem na tela avulsa de Administração › Área de SOC. É migração de
 * hospedagem, não reescrita: a lógica de integrantes e de políticas de
 * alerta continua inteira dentro deles. Detalhe em vez de expandir na linha
 * porque os dois componentes são grandes — comprimidos numa linha de lista
 * ficariam ilegíveis, e a aba já vive num card que pode usar a largura toda.
 *
 * `timeAberto` guarda só o ID, desacoplado de `times.data` — o detalhe
 * sobrevive a um refetch da lista (staleTime, foco de aba, invalidação após
 * salvar) sem fechar sozinho. O time aberto (`timeAtual`) é procurado de novo
 * a cada render: é assim que o aviso de time DESLIGADO abaixo pega o `has_soc`
 * mais recente, mesmo que o tenant tenha perdido o SOC depois do gestor já
 * ter aberto o detalhe.
 *
 * Rodada 1 de revisão (Important 2): `_exigir_soc_por_time` no servidor checa
 * permissão, não `is_active`/`has_soc` — então leitura E gravação continuam
 * funcionando normalmente num time desligado. Não corrompe dado, mas sem
 * aviso a tela deixa o gestor editar integrantes e políticas de um time cujas
 * regras de roteamento estão desativadas, sem uma palavra sobre isso.
 *
 * Task 3 (fecha o ciclo da aba): faltava CRIAR. Duas decisões:
 *
 * 1. O botão "Criar time" fica FORA do `EstadoDeConsulta` — não dentro do
 *    ramo de sucesso da lista. Organização sem nenhum time de SOC ainda cai
 *    no `estadoVazio`, que SUBSTITUI os `children` inteiros (é assim que
 *    `EstadoDeConsulta` funciona); se o botão estivesse dentro, o caso mais
 *    comum de precisar criar — organização que ainda não tem nenhum time —
 *    seria justamente o caso em que não haveria como.
 * 2. O filtro de tenant oferecido é `has_soc && !ja_tem_soc`, mas "oferecido"
 *    não é "listado": tenant inelegível não some do Select — aparece
 *    desabilitado, com o motivo, no mesmo espírito do que a tela avulsa
 *    (`SocTeamsAdmin.tsx`) já faz com `ja_tem_soc`. Esconder faz o operador
 *    procurar o que não vê; desabilitado com motivo diz o que fazer (pedir a
 *    habilitação do SOC à plataforma, ou usar o time que já existe).
 *
 * Depois de criar, o detalhe do time recém-criado abre sozinho
 * (`setTimeAberto`): quem acabou de criar quer pôr gente, e era justamente
 * esse passo que faltava em produção — o time do cliente existiu com zero
 * membros nos três níveis porque não havia caminho de volta até o editor.
 */
export function AbaTimesDeSoc({ orgId }: { orgId: number }) {
  const times = useSocTeams()
  const tenants = useSocTenants(orgId)
  const criar = useCriarSocTeam()

  const [timeAberto, setTimeAberto] = useState<number | null>(null)
  const [criarAberto, setCriarAberto] = useState(false)
  const [tenantId, setTenantId] = useState<number | null>(null)
  const [nome, setNome] = useState('')

  // Pela organizacao ATUAL do tenant, nao pela de origem do time (TASK-11546).
  const daOrg = (times.data?.items ?? []).filter((t) => t.organizacoes_do_tenant.includes(orgId))
  const timeAtual = times.data?.items.find((t) => t.id === timeAberto) ?? null

  function abrirCriar() {
    setTenantId(null)
    setNome('')
    criar.reset()
    setCriarAberto(true)
  }

  function escolherTenant(id: number | null) {
    setTenantId(id)
    const t = (tenants.data ?? []).find((x) => x.id === id)
    // O nome é PRÉ-PREENCHIDO e editável — mesmo critério da tela avulsa
    // (`SocTeamsAdmin.tsx`): derivar sem deixar mudar seria impor, pedir sem
    // sugerir seria trabalho manual num valor que o banco já deriva sozinho.
    setNome(t ? nomeDerivado(t) : '')
  }

  function confirmarCriacao() {
    if (tenantId == null) return
    criar.mutate(
      { organization_id: orgId, tenant_id: tenantId, name: nome || undefined },
      {
        onSuccess: (d) => {
          setCriarAberto(false)
          setTimeAberto(d.id)
        },
      },
    )
  }

  const opcoesTenant = (tenants.data ?? []).map((t) => {
    const elegivel = t.has_soc && !t.ja_tem_soc
    // Dois motivos de recusa distintos, cada um com a própria frase — a
    // guarda de `criar_time` devolve 409 nos dois casos, mas o que o gestor
    // tem de fazer para resolver é diferente (pedir habilitação à
    // plataforma vs. simplesmente usar o time que já existe).
    //
    // A frase diz a AÇÃO, não o estado: mostrar o tenant desabilitado em vez
    // de escondê-lo só se justifica porque desabilitado DIZ O QUE FAZER, e
    // "SOC não habilitado neste tenant" (o texto anterior) apenas repetia o
    // estado — deixava o gestor sem saber a quem pedir.
    const motivo = !t.has_soc
      ? 'SOC não habilitado — peça a habilitação à plataforma'
      : t.ja_tem_soc
        ? 'já possui time — abra o time existente na lista'
        : null
    const rotuloBase = `${t.name}-${t.environment.toUpperCase()}`
    return {
      value: String(t.id),
      label: motivo ? `${rotuloBase} — ${motivo}` : rotuloBase,
      disabled: !elegivel,
    }
  })

  // `isError` sem tratamento cai no mesmo `[]` de "a org de fato nao tem time
  // nenhum" — falha de rede/403/500 virava a afirmacao "Nenhum tenant desta
  // organizacao tem time de SOC", que manda o gestor pedir habilitacao de
  // algo que pode muito bem ja existir. `EstadoDeConsulta` (o mesmo padrao
  // usado no resto do app) distingue os dois.
  return (
    <>
      {timeAberto !== null ? (
        <Stack gap="md">
          <Group justify="space-between">
            <Button variant="subtle" onClick={() => setTimeAberto(null)}>
              Voltar para a lista
            </Button>
            {timeAtual && (
              <Badge color={timeAtual.is_active ? 'inovareTeal' : 'gray'}>
                {timeAtual.is_active ? 'Ativo' : 'Desligado'}
              </Badge>
            )}
          </Group>
          {/* Important 2 (rodada 1): o gate do servidor checa permissão, não
              `is_active` — editor e configuração continuam funcionando (leitura
              E gravação) num time desligado. Sem este aviso, o gestor mexe em
              integrantes e políticas de um time cujas regras de roteamento
              estão desativadas, sem uma palavra sobre isso.

              O texto afirma SÓ o que o payload sustenta. Ele traz `is_active`,
              não a CAUSA — e `is_active=false` tem duas origens:
              `desligar_soc_do_tenant` (o SOC do tenant caiu) e o
              `DELETE /admin/alert-teams/{id}`, que hoje alcança time de SOC
              porque `teams.list_teams` não filtra `kind` (TASK-10259, adiada).
              Cravar "o SOC deste tenant foi desligado" era falso no segundo
              caminho, e falso justamente nas duas metades que guiam a ação: o
              SOC segue habilitado, e o gestor pediria para reabilitar o que já
              está habilitado. O que É verdade nos dois: `deactivate_team`
              desativa `team` e `team_level`, então o time e as regras dele
              estão desativados. */}
          {timeAtual && !timeAtual.is_active && (
            <Alert color="red" title="Este time não está cobrindo ninguém">
              O time está desativado, e com ele as regras de roteamento de
              alerta. Editar integrantes ou configuração aqui não reativa a
              cobertura — para reativar, peça à plataforma que verifique este
              time e o SOC deste tenant.
            </Alert>
          )}
          <EditorDeIntegrantes teamId={timeAberto} />
          <ConfiguracaoDeAlertas teamId={timeAberto} />
          <TetoDeLigacoes
            teamId={timeAberto}
            valorAtual={timeAtual?.max_ligacoes_por_hora ?? null}
          />
        </Stack>
      ) : (
        <Stack gap="md">
          <Group justify="flex-end">
            <Button onClick={abrirCriar}>Criar time</Button>
          </Group>
          <EstadoDeConsulta
            consulta={times}
            vazio={daOrg.length === 0}
            estadoVazio={
              // A instrução completa importa: o caso mais comum de zero times
              // é a organização em que NENHUM tenant tem SOC habilitado, e aí
              // "Criar time" abre um seletor inteiramente desabilitado. Mandar
              // clicar sem dizer a quem pedir a habilitação é mandar bater
              // numa porta fechada — a tela avulsa dizia isso e esta perdeu.
              <Text c="dimmed" size="sm">
                Nenhum tenant desta organização tem time de SOC ainda. Use "Criar
                time" acima para montar o primeiro — só são elegíveis os tenants
                com SOC habilitado pela plataforma e que ainda não têm time. Se
                nenhum tenant aparecer elegível, peça à plataforma que habilite o
                SOC do tenant que você quer monitorar.
              </Text>
            }
          >
            <DataCard flush title="Times de SOC">
              <Stack gap="sm" p="md">
                {daOrg.map((t) => (
                  <Stack
                    key={t.id}
                    gap="xs"
                    p="md"
                    style={{
                      border: '1px solid var(--ai-border-light)',
                      borderRadius: 'var(--mantine-radius-md)',
                    }}
                  >
                    <Group justify="space-between" align="flex-start">
                      <div>
                        <Text fw={600}>{t.name}</Text>
                        <TenantDoTime time={t} />
                      </div>
                      <Group gap="sm">
                        {/* O time DESLIGADO continua na lista — sumir é o defeito
                            que a 5b.1 consertou. Mas não pode parecer coberto:
                            `is_active` é quem manda, não a presença na lista nem
                            a contagem abaixo. */}
                        <Badge color={t.is_active ? 'inovareTeal' : 'gray'}>
                          {t.is_active ? 'Ativo' : 'Desligado'}
                        </Badge>
                        {/* Toda linha tem o mesmo texto "Abrir" — sem
                            `aria-label`, leitor de tela anuncia "Abrir, Abrir,
                            Abrir" sem dizer de qual time (Important 1, rodada 1).
                            Nome do time no aria-label desambigua tanto para
                            acessibilidade quanto para `getByRole` em lista com
                            mais de um time — o caso normal desta aba. */}
                        <Button
                          size="xs"
                          variant="light"
                          aria-label={`Abrir ${t.name}`}
                          onClick={() => setTimeAberto(t.id)}
                        >
                          Abrir
                        </Button>
                      </Group>
                    </Group>

                    <Group gap="lg">
                      {NIVEIS.map((n) => {
                        const quantos = t.members_by_level?.[n] ?? 0
                        return (
                          <Text key={n} size="sm" c={quantos === 0 ? 'orange' : undefined}>
                            {quantos === 0 ? `Nível ${n}: ninguém` : `Nível ${n}: ${quantos}`}
                          </Text>
                        )
                      })}
                    </Group>
                  </Stack>
                ))}
              </Stack>
            </DataCard>
          </EstadoDeConsulta>
        </Stack>
      )}

      <Modal opened={criarAberto} onClose={() => setCriarAberto(false)} title="Criar time de SOC">
        <Stack gap="md">
          <Select
            label="Tenant"
            description="O tenant não muda depois de criado. Para atuar em outro tenant, crie outro time."
            placeholder={
              tenants.isError
                ? 'Não foi possível carregar os tenants'
                : 'Selecione o tenant a monitorar'
            }
            error={tenants.isError ? 'Não foi possível carregar os tenants.' : undefined}
            value={tenantId ? String(tenantId) : null}
            onChange={(v) => escolherTenant(v ? Number(v) : null)}
            data={opcoesTenant}
          />
          <TextInput
            label="Nome do time"
            description="Sugerido a partir do tenant, e editável."
            value={nome}
            onChange={(e) => setNome(e.currentTarget.value)}
            disabled={tenantId == null}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setCriarAberto(false)}>
              Cancelar
            </Button>
            <Button
              disabled={tenantId == null}
              loading={criar.isPending}
              onClick={confirmarCriacao}
            >
              Criar
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}
